import { scry, scryImageUrl } from "@/lib/scryClient";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Button } from "@/components/ui/button";

type WishDoc = {
  id: string;
  scryfall_id?: string | null;
  name: string;
  set?: string | null;
  number?: string | null;
  image?: string | null;
  oracle_text?: string | null;
};

type CardVariant = {
  id: string;
  name: string;
  set: string;
  collector_number: string;
  image: string | null;
  oracle_text: string | null;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: WishDoc | null;
  uid: string | null;
  onUpdated: (partial: Partial<WishDoc> | null) => void;
  onDeleted?: (id: string) => void;
}

const DEBOUNCE_MS = 400;

export default function WishlistVariantModal({
  open,
  onOpenChange,
  item,
  uid,
  onUpdated,
  onDeleted,
}: Props) {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState<CardVariant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CardVariant | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const debounceRef = useRef<number | null>(null);

  // Seed on open
  useEffect(() => {
    if (open && item) {
      setSearch(item.name);
      setSelected(null);
      setVariants([]);
      setError(null);
      setSaving(false);
      setDeleting(false);
    }
  }, [open, item]);

  const runSearch = useCallback(
    async (q: string) => {
      if (!open) return;
      const trimmed = q.trim();
      if (!trimmed) {
        setVariants([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const url = `https://api.scryfall.com/cards/search?q=%21%22${encodeURIComponent(
          trimmed,
        )}%22&unique=prints&order=released`;
        const res = await fetch(url);
        const data = await res.json();
        const list: CardVariant[] = (data?.data || []).slice(0, 60).map((c: any) => {
          const img =
            c.image_uris?.normal ||
            (Array.isArray(c.card_faces) && c.card_faces[0]?.image_uris?.normal) ||
            null;
          const oracle =
            c.oracle_text ||
            (Array.isArray(c.card_faces)
              ? c.card_faces
                  .map((f: any) => f.oracle_text)
                  .filter(Boolean)
                  .join("\n//\n")
              : null);
          return {
            id: c.id,
            name: c.name,
            set: c.set?.toUpperCase?.() || c.set,
            collector_number: c.collector_number,
            image: img,
            oracle_text: oracle,
          };
        });
        setVariants(list);
      } catch (e: any) {
        setError(e?.message || "Search failed");
      } finally {
        setLoading(false);
      }
    },
    [open],
  );

  // Debounce search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runSearch(search), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search, runSearch, open]);

  function close() {
    onOpenChange(false);
  }

  async function save() {
    if (!uid || !item || !selected) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", uid, "wishlist", item.id), {
        name: selected.name,
        scryfall_id: selected.id,
        set: selected.set,
        number: selected.collector_number,
        image: selected.image,
        oracle_text: selected.oracle_text,
        updatedAt: new Date(),
      });
      onUpdated({
        id: item.id,
        name: selected.name,
        scryfall_id: selected.id,
        set: selected.set,
        number: selected.collector_number,
        image: selected.image,
        oracle_text: selected.oracle_text,
      });
      close();
    } catch (e: any) {
      setError(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function removeWish() {
    if (!uid || !item) return;
    if (!confirm("Remove this card from your wishlist?")) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "users", uid, "wishlist", item.id));
      onDeleted?.(item.id);
      onUpdated(null);
      close();
    } catch (e: any) {
      setError(e?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  // Selected (or fallback item) card image + text
  function imgSrc(): string {
    return (
      selected?.image ||
      item?.image ||
      "/images/cardback.png"
    );
  }

  const showSaveEnabled = !!selected && !saving;

  if (!open || !item) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center md:p-4"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div className="fixed inset-0 bg-black/70" aria-hidden />
      <div
        className="relative z-[101] w-full md:w-[min(95vw,950px)] h-[100dvh] md:h-auto max-h-[100dvh] md:max-h-[92vh]
          overflow-hidden md:rounded-2xl rounded-none border border-slate-700 bg-gray-950 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 flex items-center justify-between gap-2 px-3 sm:px-4 py-3 border-b border-slate-800 bg-gray-950/90 backdrop-blur">
          <div className="min-w-0">
            <div className="text-base md:text-lg font-semibold text-white truncate">
              {item.name}
            </div>
            <div className="text-xs text-slate-400 truncate">
              {(item.set || "")?.toUpperCase?.()} {item.number ? `• #${item.number}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={removeWish}
              disabled={deleting || saving}
              className="bg-red-600/80 hover:bg-red-600 text-white"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={!showSaveEnabled}
              className="bg-emerald-600/80 hover:bg-emerald-600 text-white disabled:opacity-40"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={close}
              className="border-white/20 text-slate-200 hover:bg-white/10"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left: Image + oracle */}
            <div className="bg-black p-0 md:p-4 flex flex-col gap-3">
              <div className="w-full aspect-[3/4] bg-black overflow-hidden md:rounded-lg">
                <img
                  src={imgSrc()}
                  alt={selected?.name || item.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="px-3 md:px-0 pb-4">
                <h3 className="text-xs font-semibold tracking-wide text-slate-400 mb-1">
                  Oracle Text
                </h3>
                <div className="whitespace-pre-line text-sm text-slate-200 min-h-[80px]">
                  {selected?.oracle_text ||
                    item.oracle_text ||
                    "Select a version to view oracle text."}
                </div>
              </div>
            </div>

            {/* Right: Variant selection */}
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-400">
                  Search Version
                </label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, set code, etc."
                  className="mt-1 w-full rounded-md border border-slate-700 bg-black/50 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div className="h-5 text-xs text-slate-400 flex items-center">
                {loading
                  ? "Loading…"
                  : error
                    ? <span className="text-red-400">{error}</span>
                    : `${variants.length} result${variants.length === 1 ? "" : "s"}`}
              </div>

              <ul className="grid gap-2 sm:grid-cols-2 max-h-[46vh] overflow-y-auto pr-1">
                {variants.map((v) => {
                  const active = selected?.id === v.id;
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(v)}
                        className={`w-full text-left rounded-md border px-2 py-2 bg-white/5 hover:bg-white/10 transition text-xs ${
                          active
                            ? "border-emerald-400/70 ring-1 ring-emerald-400/50"
                            : "border-white/10"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-14 bg-black/60 rounded overflow-hidden flex items-center justify-center">
                            {v.image ? (
                              <img
                                src={v.image}
                                alt={v.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <span className="text-[10px] text-slate-500">
                                No image
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{v.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">
                              {v.set} • #{v.collector_number}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
                {!loading && variants.length === 0 && !error && (
                  <div className="text-xs text-slate-500 px-1 py-2">
                    No matches.
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

